import React, { useEffect, useMemo, useState } from "react";
import { Check, Eye, Loader2, RefreshCw } from "lucide-react";

import PrivateReferenceImage from "../masterOrder/PrivateReferenceImage";
import {
  listBrandReferences,
  runVisionAnalysisCapability,
} from "../../lib/brandStudioApi";

const GROUP_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  materiality: "Материалы / фактуры",
  photography: "Фотография",
  graphic_language: "Графический язык",
  composition: "Композиция",
  applications: "Носители",
};

function errorText(error) {
  const detail = error?.response?.data?.detail;
  const code =
    typeof detail === "object" && detail
      ? detail.code
      : typeof detail === "string"
        ? detail
        : "";
  const map = {
    reference_not_found: "Референс не найден.",
    reference_binary_missing: "У этого референса нет доступного исходного файла.",
    reference_binary_failed: "Не удалось прочитать файл референса.",
    vision_test_already_running: "Анализ этого референса уже выполняется.",
    vision_test_already_succeeded:
      "Этот референс уже был успешно проверен. Выбери другой, чтобы не делать повторный платный вызов.",
    vision_test_retry_required:
      "Предыдущая попытка завершилась ошибкой. Нажми «Повторить анализ».",
    vision_openai_not_configured: "Vision provider пока не настроен на Preview.",
    vision_openai_request_failed: "Vision provider не принял запрос.",
    vision_yandex_request_failed: "Yandex Vision provider не принял запрос.",
    vision_output_invalid: "Модель вернула ответ не по ожидаемой структуре.",
    vision_response_incomplete: "Модель не завершила структурированный ответ.",
  };
  return map[code] || code || "Не удалось выполнить Vision capability test.";
}

function money(micros, currency) {
  if (micros === null || micros === undefined || !currency) return null;
  return `${(Number(micros) / 1_000_000).toFixed(6)} ${currency}`;
}

export default function VisionCapabilityOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [references, setReferences] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    listBrandReferences()
      .then((items) => {
        if (cancelled) return;
        const next = items || [];
        setReferences(next);
        setSelectedId(next[0]?.id || "");
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
        setError("Не удалось открыть референсы для VU3 QA.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(
    () => references.find((item) => item.id === selectedId) || null,
    [references, selectedId],
  );

  const grouped = useMemo(() => {
    const map = {};
    for (const observation of result?.observations || []) {
      if (!map[observation.group]) map[observation.group] = [];
      map[observation.group].push(observation);
    }
    return map;
  }, [result]);

  const run = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const next = await runVisionAnalysisCapability(selectedId, retry);
      setResult(next);
      setRetry(false);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      const code =
        typeof detail === "object" && detail ? detail.code : "";
      setRetry(code === "vision_test_retry_required");
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#A88A68] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="vision-capability-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        OWNER QA · VU3
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Проверка: действительно ли система видит референс?
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Это техническая проверка одного изображения. Vision должна только описать,
        что реально видно: цвет, материал, фотографию, графику, композицию и носители.
        Она не должна решать, что тебе нравится, и не должна создавать дизайн.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#806B55]">
          <Loader2 size={14} className="animate-spin" /> Открываю референсы…
        </div>
      )}

      {status === "ready" && references.length === 0 && (
        <p className="mt-5 text-[12px] text-[#806B55]">
          Нет сохранённых референсов для capability test.
        </p>
      )}

      {status === "ready" && references.length > 0 && (
        <>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#927A5B]">
              Выбери один референс
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6">
              {references.map((reference, index) => {
                const active = reference.id === selectedId;
                return (
                  <button
                    key={reference.id}
                    type="button"
                    onClick={() => {
                      if (busy) return;
                      setSelectedId(reference.id);
                      setResult(null);
                      setError("");
                      setRetry(false);
                    }}
                    aria-pressed={active}
                    className={
                      "relative aspect-[4/5] overflow-hidden rounded-[12px] border bg-[#F3EBDD] p-1 " +
                      (active
                        ? "border-[#6E4D35] ring-2 ring-[#6E4D35]/20"
                        : "border-[#DCCDB8]")
                    }
                    data-testid={"vision-capability-reference-" + index}
                  >
                    <PrivateReferenceImage
                      reference={reference}
                      className="h-full w-full rounded-[9px] object-cover"
                    />
                    <span className="absolute bottom-1.5 left-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[9px] text-[#4A3728]">
                      {index + 1}
                    </span>
                    {active && (
                      <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[#3D2A20] text-white">
                        <Check size={11} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {selected && (
            <div className="mt-5 grid gap-4 md:grid-cols-[190px_1fr]">
              <div className="overflow-hidden rounded-[14px] border border-[#DCCDB8] bg-[#F3EBDD] p-2">
                <PrivateReferenceImage
                  reference={selected}
                  className="block max-h-[230px] w-full rounded-[10px] object-contain"
                />
              </div>
              <div>
                <div className="text-[11px] text-[#806B55]">
                  Анализируется только этот референс. Остальные изображения сейчас не отправляются Vision provider.
                </div>
                {selected.note && (
                  <div className="mt-3 rounded-[12px] bg-white px-3 py-2 text-[11px] leading-5 text-[#655141]">
                    Твоя заметка к референсу: «{selected.note}»
                  </div>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={run}
                  className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
                  data-testid="vision-capability-run-v1"
                >
                  {busy ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Смотрю референс…
                    </>
                  ) : retry ? (
                    <>
                      <RefreshCw size={14} />
                      Повторить анализ
                    </>
                  ) : (
                    <>
                      <Eye size={14} />
                      Проверить этот референс
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 border-t border-[#E2D5C3] pt-5" data-testid="vision-capability-result-v1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> Анализ завершён
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              {result.provider} · {result.model}
            </span>
            {money(result.actual_cost_micros, result.currency) && (
              <span className="text-[10.5px] text-[#8A745E]">
                · {money(result.actual_cost_micros, result.currency)}
              </span>
            )}
          </div>

          <div className="mt-4 rounded-[13px] border border-[#DCCDB8] bg-white px-4 py-3 text-[11px] leading-5 text-[#655141]">
            Все пункты ниже пока имеют статус <strong>«визуально присутствует»</strong>.
            Это слабое evidence: ни один пункт ещё не становится решением бренда автоматически.
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(grouped).map(([group, observations]) => (
              <article
                key={group}
                className="rounded-[14px] border border-[#DED0BC] bg-white p-4"
              >
                <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                  {GROUP_LABELS[group] || group}
                </div>
                <div className="mt-2 space-y-2">
                  {observations.map((item) => (
                    <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
                      • {item.value}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 text-[10.5px] leading-5 text-[#8A745E]">
            Вход: {result.input_units || 0} · выход: {result.output_units || 0}.
            Для VU3 PASS оцениваем только точность наблюдений по изображению.
          </div>
        </div>
      )}
    </section>
  );
}
